import { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse, CancelTokenSource } from 'axios'
import { IdData } from '@/api/model'

export interface AxiosResponseExt {
  $axios: AxiosResponse
  $page?: PageResponse
}

/**
 * Accès à une resource d'API.
 *
 * La réponse Axios reste disponible via la propriété `$axios`.
 */
export abstract class AbstractResource {
  protected axios: AxiosInstance

  constructor (axios: AxiosInstance) {
    this.axios = axios
  }

  protected wrapPromise (axiosPromise: AxiosPromise) {
    return new Promise((resolve, reject) => {
      axiosPromise.then((axiosResponse: AxiosResponse) => {
        const data = axiosResponse.data
        if (typeof data === 'object') {
          data.$axios = axiosResponse
          delete data.$axios.data

          const headers = axiosResponse.headers

          if ('pagination-page' in headers || 'pagination-itemsperpage' in headers || 'pagination-itemstotalcount' in headers) {
            data.$page = {
              page: parseInt(headers['pagination-page'], 10),
              itemsPerPage: parseInt(headers['pagination-itemsperpage'], 10),
              itemsTotalCount: parseInt(headers['pagination-itemstotalcount'], 10)
            }
          }

          resolve(data)
        } else {
          resolve(axiosResponse.data)
        }
      }).catch(reject)
    })
  }
}

export abstract class AbstractGetEntityResource<T, K> extends AbstractResource {
  private prefix: string

  constructor (axios: AxiosInstance, prefix: string) {
    super(axios)
    this.prefix = prefix
  }

  getPath (id?: K): string {
    if (id === undefined) {
      return this.prefix
    } else {
      let idPath = this.getIdPathRepresentation(id)
      return `${this.prefix}/${idPath}`
    }
  }

  getIdPathRepresentation (id: K): string {
    return '' + id
  }

  get (id: K, cancelTokenSource?: CancelTokenSource): Promise<T & AxiosResponseExt> {
    let path = this.getPath(id)
    let cancelOptions = cancelTokenSource ? { cancelToken: cancelTokenSource.token } : undefined
    let promise = this.wrapPromise(this.axios.get(path, cancelOptions))
    return promise as Promise<T & AxiosResponseExt>
  }
}

export abstract class AbstractEntityResource<T, K> extends AbstractGetEntityResource<T, K> {
  abstract getObjectId (object: T): K

  getPathFromObject (object?: T) {
    if (object === undefined) {
      return this.getPath()
    } else {
      let id = this.getObjectId(object)
      return this.getPath(id)
    }
  }

  getFromObject (object: T): Promise<T & AxiosResponseExt> {
    return this.get(this.getObjectId(object))
  }

  list (cancelTokenSource?: CancelTokenSource): Promise<T[] & AxiosResponseExt> {
    let cancelOptions = cancelTokenSource ? { cancelToken: cancelTokenSource.token } : undefined
    let promise = this.wrapPromise(this.axios.get(this.getPath(), cancelOptions))
    return promise as Promise<T[] & AxiosResponseExt>
  }

  create (object: T): Promise<T & AxiosResponseExt> {
    return this.wrapPromise(this.axios.post(this.getPath(), object)) as Promise<T & AxiosResponseExt>
  }

  deleteFromObject (object: T): Promise<T & AxiosResponseExt> {
    return this.delete(this.getObjectId(object))
  }

  delete (id: K): Promise<T & AxiosResponseExt> {
    let path = this.getPath(id)
    return this.wrapPromise(this.axios.delete(path)) as Promise<T & AxiosResponseExt>
  }

  update (object: T): Promise<T & AxiosResponseExt> {
    let path = this.getPathFromObject(object)
    return this.wrapPromise(this.axios.put(path, object)) as Promise<T & AxiosResponseExt>
  }
}

export abstract class AbstractDataResource<T extends IdData<any>> extends AbstractEntityResource<T, string> {
  getObjectId (object: T): string {
    return object.id
  }
}

export class DefaultDataResource<T extends IdData<any>> extends AbstractDataResource<T> {
  constructor (axios: AxiosInstance, dataName: string) {
    super(axios, dataName)
  }
}

export interface Page {
  page: number
  itemsPerPage: number
}

export interface PageResponse extends Page {
  itemsTotalCount: number
}

export function configurePage (page?: Page, config?: AxiosRequestConfig) {
  if (!page) {
    return config
  }

  if (!config) {
    config = {}
  }

  if (!config.headers) {
    config.headers = {}
  }

  config.headers['Pagination-Page'] = page.page
  config.headers['Pagination-ItemsPerPage'] = page.itemsPerPage

  return config
}

export function configureCancel (cancelTokenSource?: CancelTokenSource, config?: AxiosRequestConfig) {
  if (!cancelTokenSource) {
    return config
  }

  if (!config) {
    config = {}
  }

  config.cancelToken = cancelTokenSource.token
  return config
}
